import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiMapPin, FiPhone, FiMail, FiUser, FiTruck, FiMessageCircle, FiFileText, FiAlertCircle, FiUploadCloud } from 'react-icons/fi';
import axios from 'axios';
import { API_URL, API_BASE_URL } from '../config';
import { getAuthToken } from '../services/api';
import PortalCornerMap from '../components/PortalCornerMap';
import PortalAccessGate from '../components/PortalAccessGate';

// Staff viewing a portal send their login token so the server lets them skip the public
// last-4 gate. Public visitors have no token and are unaffected.
async function portalConfig() {
  try {
    const t = await getAuthToken();
    return t ? { withCredentials: true, headers: { Authorization: `Bearer ${t}` } } : { withCredentials: true };
  } catch (e) { return { withCredentials: true }; }
}

const NOTIF_ICONS = {
  status_change: FiCheckCircle,
  en_route: FiTruck,
  scheduled: FiClock,
  message: FiMessageCircle,
  document: FiFileText,
  completed: FiCheckCircle,
  client_request: FiMessageCircle,
};

const NOTIF_COLORS = {
  status_change: '#2563eb',
  en_route: '#16a34a',
  scheduled: '#d97706',
  message: '#7c3aed',
  document: '#6b7280',
  completed: '#16a34a',
  client_request: '#ea580c',
};

// Date-only values ("2026-08-26", from a db.Date column like job.scheduled_date)
// are calendar dates, not instants. Appending 'Z' to one builds a string the spec
// does not define, and engines disagree: Safari and Firefox return Invalid Date --
// which this portal printed at a client on 2026-08-24 -- while V8 reads it as UTC
// midnight and renders the day BEFORE. Neither is right, so parse the parts as
// written. Real timestamps still get 'Z' so they land in the viewer's local zone.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

function toDate(iso) {
  if (!iso) return null;
  const d = DATE_ONLY.test(iso)
    ? new Date(`${iso}T12:00:00`)   // local noon, so no zone offset can shift the day
    : new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(iso) {
  const d = toDate(iso);
  if (!d) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatRelative(iso) {
  const d = toDate(iso);
  if (!d) return '';
  const now = new Date();
  const diff = now - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

function ClientPortalPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [portal, setPortal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [comment, setComment] = useState('');
  const [commenterName, setCommenterName] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('portal_commenter_name') : '';
    return searchParams.get('name') || saved || '';
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  // Picked but not yet sent. On a phone, tapping the upload box opens the photo picker
  // and one more tap used to upload instantly — a client sent us a grocery-list photo
  // that way (J4743, 2026-11-02). Now they see what they picked and confirm it.
  const [pendingFile, setPendingFile] = useState(null);
  const [pendingPreview, setPendingPreview] = useState('');
  useEffect(() => () => { if (pendingPreview) URL.revokeObjectURL(pendingPreview); }, [pendingPreview]);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const ACCESS_KEY = `portal_access_${token}`;
  const [accessToken, setAccessToken] = useState(() => {
    try { return window.localStorage.getItem(ACCESS_KEY) || ''; } catch (e) { return ''; }
  });

  const loadPortal = async (at) => {
    const q = at ? `?access=${encodeURIComponent(at)}` : '';
    const res = await axios.get(`${API_URL}/public/portal/${token}${q}`, await portalConfig());
    setPortal(res.data);
    return res.data;
  };

  useEffect(() => {
    (async () => {
      try {
        await loadPortal(accessToken);
      } catch (err) {
        setError(err.response?.status === 404 ? 'This portal link is no longer active.' : 'Failed to load portal.');
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleVerified = async (newToken) => {
    try { window.localStorage.setItem(ACCESS_KEY, newToken); } catch (e) {}
    setAccessToken(newToken);
    setLoading(true);
    try { await loadPortal(newToken); } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const handleSendComment = async () => {
    if (!comment.trim() || !commenterName.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/public/portal/${token}/comment`, {
        message: comment.trim(),
        commenter_name: commenterName.trim(),
      });
      try { window.localStorage.setItem('portal_commenter_name', commenterName.trim()); } catch (e) {}
      setComment('');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
      // Reload portal to show the new comment in the feed
      await loadPortal(accessToken);
    } catch (err) {
      alert('Failed to send. Please try again.');
    }
    setSending(false);
  };

  const pickFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';   // let the same file be re-picked later
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      setUploadMsg('That file is too large (25 MB max).');
      return;
    }
    setUploadMsg('');
    setPendingFile(file);
    setPendingPreview((file.type || '').startsWith('image/') ? URL.createObjectURL(file) : '');
  };

  const cancelUpload = () => { setPendingFile(null); setPendingPreview(''); };

  const handleUpload = async () => {
    const file = pendingFile;
    if (!file) return;
    setPendingFile(null);
    setPendingPreview('');
    setUploading(true);
    setUploadMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (commenterName.trim()) fd.append('uploader_name', commenterName.trim());
      const q = accessToken ? `?access=${encodeURIComponent(accessToken)}` : '';
      const cfg = await portalConfig();
      await axios.post(`${API_URL}/public/portal/${token}/upload${q}`, fd, {
        ...cfg,
        headers: { ...(cfg.headers || {}), 'Content-Type': 'multipart/form-data' },
      });
      setUploadMsg('Uploaded. Thanks — our team has been notified.');
      await loadPortal(accessToken);
      setTimeout(() => setUploadMsg(''), 4000);
    } catch (err) {
      setUploadMsg(err.response?.data?.error || 'Upload failed. Please try again.');
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '24px', marginBottom: '12px' }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (portal && portal.needs_access) {
    return (
      <PortalAccessGate
        company={portal.company}
        subtitle={portal.job_number ? `Project ${portal.job_number}` : ''}
        verifyPath={`/public/portal/${token}/access`}
        onVerified={handleVerified}
      />
    );
  }

  if (error || !portal) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center',
          maxWidth: '400px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <FiAlertCircle size={48} color="#dc2626" />
          <h2 style={{ margin: '16px 0 8px', color: '#111827' }}>Portal Not Found</h2>
          <p style={{ color: '#6b7280', margin: 0 }}>{error || 'This link may have expired.'}</p>
        </div>
      </div>
    );
  }

  const { job, client, status_timeline, notifications, company, map_data, photos } = portal;
  const hasMap = map_data && ((map_data.photos && map_data.photos.length) ||
    (map_data.boundary && map_data.boundary.features && map_data.boundary.features.length));
  const galleryPhotos = photos || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#1e293b', color: 'white', padding: '20px 0',
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
            {company?.logo && (
              <img src={`${API_BASE_URL}${company.logo}`} alt="Logo"
                style={{ height: '40px', borderRadius: '6px' }}
                onError={(e) => e.target.style.display = 'none'} />
            )}
            <div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>{company?.name || 'North Georgia Surveying'}</div>
              <div style={{ fontSize: '12px', opacity: 0.7 }}>Client Project Portal</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '20px' }}>
        {/* Job Info Card */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>
            Project
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: '22px', color: '#111827', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {job?.job_number}
            {portal?.phase_name && (
              <span style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', background: '#f5f3ff', color: '#6d28d9', padding: '3px 10px', borderRadius: '10px' }}>
                {portal.phase_name}
              </span>
            )}
          </h2>
          {client?.name && (
            <div style={{ fontSize: '15px', color: '#374151', marginBottom: '4px' }}>
              <FiUser size={14} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
              {client.name}{client.company_name ? ` — ${client.company_name}` : ''}
            </div>
          )}
          {job?.property_address && (
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              <FiMapPin size={14} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
              {job.property_address}{job.county ? `, ${job.county} County` : ''}
            </div>
          )}
          {formatDate(job?.scheduled_date) && (
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
              <FiClock size={14} style={{ marginRight: '6px', verticalAlign: 'text-bottom' }} />
              Estimated timeframe: {formatDate(job.scheduled_date)} (subject to change)
            </div>
          )}
        </div>

        {/* Status Timeline */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '16px' }}>
            Project Status
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexWrap: 'wrap', justifyContent: 'center' }}>
            {status_timeline?.map((step, i) => {
              const isActive = step.done && (!status_timeline[i + 1] || !status_timeline[i + 1].done);
              return (
                <React.Fragment key={step.key}>
                  {i > 0 && (
                    <div style={{
                      width: isMobile ? '16px' : '30px', height: '3px',
                      backgroundColor: step.done ? '#16a34a' : '#e5e7eb',
                      flexShrink: 0,
                    }} />
                  )}
                  <div style={{ textAlign: 'center', flexShrink: 0 }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%', margin: '0 auto 4px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: step.done ? '#16a34a' : '#e5e7eb',
                      color: step.done ? 'white' : '#9ca3af',
                      border: isActive ? '3px solid #065f46' : 'none',
                      fontSize: '14px', fontWeight: '700',
                    }}>
                      {step.done ? <FiCheckCircle size={18} /> : i + 1}
                    </div>
                    <div style={{
                      fontSize: '10px', fontWeight: isActive ? '700' : '500',
                      color: step.done ? '#065f46' : '#9ca3af',
                      maxWidth: '70px',
                    }}>
                      {step.label}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Documents */}
        {((portal.documents || []).length > 0 || portal.can_upload) && (
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '12px' }}>
              Documents
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(portal.documents || []).map(doc => (
                <a
                  key={doc.id}
                  href={`${API_BASE_URL}${doc.download_url}${accessToken ? `?access=${encodeURIComponent(accessToken)}` : ''}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', backgroundColor: '#f9fafb',
                    borderRadius: '10px', textDecoration: 'none', border: '1px solid #e5e7eb',
                  }}
                >
                  <FiFileText size={20} color={doc.from_collaborator ? '#7c3aed' : '#2563eb'} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {doc.name}
                    </div>
                    {doc.caption && (
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{doc.caption}</div>
                    )}
                    <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                      {doc.uploaded_by ? `Shared by ${doc.uploaded_by}` : ''}
                      {doc.uploaded_by && doc.file_size != null ? ' · ' : ''}
                      {doc.file_size != null && (doc.file_size > 1048576
                        ? `${(doc.file_size / 1048576).toFixed(1)} MB`
                        : `${Math.max(1, Math.round(doc.file_size / 1024))} KB`)}
                    </div>
                  </div>
                  <span style={{ fontSize: '13px', color: '#2563eb', fontWeight: '600', flexShrink: 0 }}>Download</span>
                </a>
              ))}
            </div>

            {portal.can_upload && (
              <div style={{ marginTop: (portal.documents || []).length ? '14px' : 0 }}>
                {pendingFile ? (
                  <div style={{ border: '2px solid #c7d2fe', borderRadius: '10px', padding: '12px', backgroundColor: '#eef2ff' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', marginBottom: '8px', textAlign: 'center' }}>
                      Send this file to North Georgia Surveying?
                    </div>
                    {pendingPreview && (
                      <img src={pendingPreview} alt="" style={{ display: 'block', maxWidth: '100%', maxHeight: '220px', margin: '0 auto 8px', borderRadius: '6px' }} />
                    )}
                    <div style={{ fontSize: '13px', color: '#4b5563', textAlign: 'center', wordBreak: 'break-all', marginBottom: '10px' }}>
                      {pendingFile.name} · {(pendingFile.size / (1024 * 1024)).toFixed(1)} MB
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="button" onClick={cancelUpload}
                        style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                        Cancel
                      </button>
                      <button type="button" onClick={handleUpload}
                        style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', backgroundColor: '#4338ca', color: 'white', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                        Upload
                      </button>
                    </div>
                  </div>
                ) : (
                <label
                  htmlFor="portal-upload"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '12px 14px', border: '2px dashed #c7d2fe', borderRadius: '10px',
                    color: '#4338ca', fontWeight: '600', fontSize: '14px',
                    cursor: uploading ? 'wait' : 'pointer', backgroundColor: '#eef2ff',
                  }}
                >
                  <FiUploadCloud size={18} />
                  {uploading ? 'Uploading…' : 'Upload a document'}
                </label>
                )}
                <input
                  id="portal-upload" type="file" onChange={pickFile}
                  disabled={uploading} style={{ display: 'none' }}
                />
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px', textAlign: 'center' }}>
                  PDFs, images, CAD, and Office files up to 25 MB. Our team is notified automatically.
                </div>
                {uploadMsg && (
                  <div style={{ fontSize: '13px', color: '#4338ca', marginTop: '8px', textAlign: 'center' }}>{uploadMsg}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Property & Site Photos map */}
        {hasMap && (
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '12px' }}>
              Property &amp; Site Photos
            </div>
            <PortalCornerMap mapData={map_data} height={360} />
            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '10px' }}>
              Tap a numbered pin to view the photo taken at that location.
            </div>
          </div>
        )}

        {/* Project Photos gallery */}
        {galleryPhotos.length > 0 && (
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '12px' }}>
              Project Photos ({galleryPhotos.length})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
              {galleryPhotos.map((p) => (
                <a key={p.id} href={`${API_BASE_URL}${p.image_url}`} target="_blank" rel="noopener noreferrer"
                  style={{ display: 'block', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                  <img
                    src={`${API_BASE_URL}${p.image_url}`}
                    alt={p.caption || 'Project photo'}
                    loading="lazy"
                    style={{ width: '100%', height: '120px', objectFit: 'cover', display: 'block' }}
                  />
                  {p.caption && (
                    <div style={{ fontSize: '11px', color: '#6b7280', padding: '4px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {p.caption}
                    </div>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Activity Feed */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '16px' }}>
            Activity
          </div>

          {(!notifications || notifications.length === 0) ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '14px' }}>
              No updates yet. We'll post updates here as your project progresses.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {(showAllActivity ? notifications : notifications.slice(0, 5)).map((notif, i, arr) => {
                const Icon = NOTIF_ICONS[notif.notification_type] || FiMessageCircle;
                const color = NOTIF_COLORS[notif.notification_type] || '#6b7280';

                return (
                  <div key={notif.id} style={{
                    display: 'flex', gap: '14px', padding: '16px 0',
                    borderBottom: i < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                  }}>
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                      backgroundColor: `${color}15`, color: color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={18} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                        {notif.title}
                      </div>
                      {notif.commenter_name && notif.notification_type === 'client_request' && (
                        <div style={{ fontSize: '12px', color: '#ea580c', fontWeight: '600', marginTop: '2px' }}>
                          {notif.commenter_name}
                        </div>
                      )}
                      {notif.message && (
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px', lineHeight: '1.4' }}>
                          {notif.message}
                        </div>
                      )}

                      {/* Crew bios for en_route */}
                      {notif.notification_type === 'en_route' && notif.crew && (
                        <div style={{
                          marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px',
                        }}>
                          {notif.crew.map((member, j) => (
                            <div key={j} style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '8px 12px', backgroundColor: '#f0fdf4',
                              borderRadius: '8px', border: '1px solid #bbf7d0',
                            }}>
                              {member.photo_url ? (
                                <img
                                  src={`${API_BASE_URL}${member.photo_url}`}
                                  alt={member.name}
                                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <div style={{
                                  width: '40px', height: '40px', borderRadius: '50%',
                                  backgroundColor: '#16a34a', color: 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '16px', fontWeight: '700',
                                }}>
                                  {member.name?.charAt(0) || '?'}
                                </div>
                              )}
                              <div>
                                <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>
                                  {member.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#16a34a' }}>
                                  {member.role}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                        {formatRelative(notif.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {notifications && notifications.length > 5 && (
            <button
              onClick={() => setShowAllActivity(v => !v)}
              style={{
                marginTop: '14px', width: '100%', padding: '10px', borderRadius: '10px',
                border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151',
                fontSize: '14px', fontWeight: '600', cursor: 'pointer',
              }}
            >
              {showAllActivity ? 'Show less' : `Show all activity (${notifications.length})`}
            </button>
          )}
        </div>

        {/* Send a Request */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '12px' }}>
            {portal.viewer_role === 'collab' ? 'Message the Team' : 'Send Us a Request'}
          </div>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 12px', lineHeight: '1.4' }}>
            {portal.viewer_role === 'collab'
              ? 'Coordinating on this project? Send a note to our team and it will appear in the activity thread above.'
              : "Need something scheduled or have a question about your project? Let us know below and we'll get back to you."}
          </p>
          <input
            type="text"
            value={commenterName}
            onChange={(e) => setCommenterName(e.target.value)}
            placeholder="Your name *"
            style={{
              width: '100%', padding: '12px', border: '1px solid #d1d5db',
              borderRadius: '10px', fontSize: '15px', boxSizing: 'border-box',
              fontFamily: 'inherit', marginBottom: '10px',
            }}
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g., Need anchor bolt staking on Building 3 next Tuesday..."
            rows={3}
            style={{
              width: '100%', padding: '12px', border: '1px solid #d1d5db',
              borderRadius: '10px', fontSize: '15px', boxSizing: 'border-box',
              fontFamily: 'inherit', resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
            {sent ? (
              <div style={{ color: '#16a34a', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FiCheckCircle size={16} /> Request sent! We'll review it shortly.
              </div>
            ) : (
              <div />
            )}
            <button
              onClick={handleSendComment}
              disabled={!comment.trim() || !commenterName.trim() || sending}
              style={{
                padding: '12px 24px',
                backgroundColor: comment.trim() && commenterName.trim() && !sending ? '#2563eb' : '#9ca3af',
                color: 'white', border: 'none', borderRadius: '10px',
                cursor: comment.trim() && commenterName.trim() && !sending ? 'pointer' : 'not-allowed',
                fontSize: '15px', fontWeight: '600',
              }}
            >
              {sending ? 'Sending...' : (portal.viewer_role === 'collab' ? 'Send Message' : 'Send Request')}
            </button>
          </div>
        </div>

        {/* Contact Card */}
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', padding: '24px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '40px',
        }}>
          <div style={{ fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '12px' }}>
            Contact Us
          </div>
          {company?.phone && (
            <a href={`tel:${company.phone}`} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
              backgroundColor: '#eff6ff', borderRadius: '8px', textDecoration: 'none',
              color: '#2563eb', fontWeight: '600', fontSize: '15px', marginBottom: '8px',
            }}>
              <FiPhone size={18} /> {company.phone}
            </a>
          )}
          {company?.email && (
            <a href={`mailto:${company.email}`} style={{
              display: 'flex', alignItems: 'center', gap: '10px', padding: '10px',
              backgroundColor: '#f9fafb', borderRadius: '8px', textDecoration: 'none',
              color: '#374151', fontSize: '14px',
            }}>
              <FiMail size={16} /> {company.email}
            </a>
          )}
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', padding: '20px', color: '#9ca3af', fontSize: '12px' }}>
          Powered by SurveyPro &middot; {company?.name}
        </div>
      </div>
    </div>
  );
}

export default ClientPortalPage;
